(ns client-portal.client.user-management.data
  (:require [clojure.string :as s]
            [shared.core.reactive :as re]
            [shared.core.router :as router]
            [shared.core.util :as util]))

(def root-path [:user-management])
(def roles-path (conj root-path :roles))
(def filters-path (conj root-path :filters))
(def request-path (conj root-path :request))

(def sections
  [{:id "users"
    :label "Users"
    :icon-type :UserGroupsIcon}
   {:id "organizations"
    :label "Organizations"
    :icon-type :TreeIcon}
   {:id "roles"
    :label "Roles"
    :icon-type :RolesIcon}
   {:id "permissions"
    :label "Permissions"
    :icon-type :UserProtectionIcon}])

(defn- body [resp]
  (or (some-> resp :body (util/->kebab-case-keywords)) []))

(defn current-tenant
  "The tenant picked in the URL, or nil for the server's default"
  [db]
  (not-empty (:tenant (router/current-query-params db))))

(re/reg-sub :user-management/current-tenant- current-tenant)

(defn tenant-path
  "Where data that belongs to one tenant is kept, so switching tenants never shows another tenant's"
  [tenant & ks]
  (into (conj root-path :tenants tenant) ks))

(defn tenant-data [db & ks]
  (get-in db (apply tenant-path (current-tenant db) ks)))

(defn with-tenant [url tenant]
  (cond-> url
          tenant (str (if (s/includes? url "?") "&" "?") "tenant=" (js/encodeURIComponent tenant))))

(defn selected-organization-id
  "The organization the Users list is narrowed to, kept in the URL"
  [db]
  (some->> (:organization (router/current-query-params db))
           (re-matches #"\d+")
           (js/parseInt)))

(re/reg-event-db
  :user-management/go re/middleware
  (fn [db [route-k path-params]]
    (let [query (select-keys (router/current-query-params db) [:tenant :organization])]
      (re/dispatch [:router/go (cond-> [route-k path-params]
                                       (not-empty query) (conj query))]))
    db))

(re/reg-event-db
  :user-management/set-tenant re/middleware
  (fn [db [tenant-id]]
    ;; Organizations belong to a tenant, so the one picked in the old tenant doesn't carry over
    (re/dispatch [:router/go-with-merged-query-params {:tenant (str tenant-id)
                                                       :organization nil}])
    db))

(re/reg-event-db
  :user-management/set-organization re/middleware
  (fn [db [tenant-organization-id]]
    (re/dispatch [:router/go-with-merged-query-params {:organization (some-> tenant-organization-id str)}])
    db))

(defn- reg-list
  "A loader + lazily loading sub for one of the lists, kept per tenant when `tenant?`"
  [k url & {:keys [xf tenant?]}]
  (let [loader-id (keyword "user-management" (str "load-" (name k)))]
    (re/reg-auth0-loader
      loader-id
      (fn [[tenant]]
        (if tenant?
          (tenant-path tenant k)
          (conj root-path k)))
      (fn [_ tenant]
        {:method :get
         :url (cond-> url
                      tenant? (with-tenant tenant))})
      :transf (comp (or xf identity) body)
      :error-transf (constantly :error))
    (re/reg-sub
      (keyword "user-management" (str (name k) "-"))
      (fn [db]
        (let [tenant (when tenant?
                       (current-tenant db))]
          (or (get-in db (if tenant?
                           (tenant-path tenant k)
                           (conj root-path k)))
              (re/dispatch-load [loader-id tenant])))))))

(reg-list :users "/api/user-management/users" :tenant? true)
(reg-list :organizations "/api/user-management/organizations" :tenant? true)
(reg-list :invitations "/api/user-management/invitations" :tenant? true)
(reg-list :tenant-list "/api/user-management/tenants")
;; Only the Operator Portal is managed here for now. Access to other applications is left as it is when saving.
(reg-list :applications "/api/user-management/applications"
          :xf (fn [applications]
                (->> applications
                     (filter #(= "client-portal" (:external-id %)))
                     (mapv #(assoc % :label "Operator Portal")))))
(reg-list :roles "/api/user-management/roles")
(reg-list :permissions "/api/user-management/permissions")
(reg-list :concepts "/api/user-management/concepts")

(defn- reg-tenant-item
  "A loader + lazily loading sub for one thing in the current tenant, like a user, by `id`"
  [k url-fn & {:keys [transf]}]
  (let [loader-id (keyword "user-management" (str "load-" (name k)))]
    (re/reg-auth0-loader
      loader-id
      (fn [[id tenant]]
        (tenant-path tenant k id))
      (fn [_ id tenant]
        {:method :get
         :url (with-tenant (url-fn id) tenant)})
      :transf (or transf body)
      :error-transf (constantly :error)
      :quiet-fail? true)
    (re/reg-sub
      (keyword "user-management" (str (name k) "-"))
      (fn [db [_ id]]
        (when id
          (let [tenant (current-tenant db)]
            (or (get-in db (tenant-path tenant k id))
                (re/dispatch-load [loader-id id tenant]))))))))

(reg-tenant-item :user #(str "/api/user-management/users/" %))
(reg-tenant-item :user-connections #(str "/api/user-management/users/" % "/connections"))
(reg-tenant-item :organization-connections #(str "/api/user-management/organizations/" % "/connections"))
(reg-tenant-item :location-access-options #(str "/api/user-management/concepts/" % "/location-access-options")
                 :transf (fn [resp]
                           (or (some-> resp :body (util/->kebab-case-keywords))
                               {})))

(defn rows
  "A loaded list, or nil while it's loading or when it failed to"
  [v]
  (when (sequential? v)
    v))

(defn by-id [k coll]
  (->> (rows coll)
       (map (juxt k identity))
       (into {})))

(re/reg-sub
  :user-management/organizations-by-id-
  :<- [:user-management/organizations-]
  (fn [organizations]
    (by-id :tenant-organization-id organizations)))

(re/reg-sub
  :user-management/roles-by-id-
  :<- [:user-management/roles-]
  (fn [roles]
    (by-id :id roles)))

(re/reg-sub
  :user-management/applications-by-id-
  :<- [:user-management/applications-]
  (fn [applications]
    (by-id :id applications)))

(re/reg-sub
  :user-management/concepts-by-id-
  :<- [:user-management/concepts-]
  (fn [concepts]
    (by-id :id concepts)))

(defn manager?
  "Manages the people in their own organization, rather than the whole tenant as an admin does"
  [user-roles]
  (and (contains? user-roles :user-manager)
       (not (contains? user-roles :admin))))

(re/reg-sub :user-management/manager?- :<- [:account/user-roles-] manager?)

(defn grantable-role-ids
  "A manager gives and takes away only the roles they have themselves. nil for an admin, who can give any."
  [user-roles roles]
  (when (manager? user-roles)
    (->> (rows roles)
         (filter #(contains? user-roles (keyword (:label %))))
         (map :id)
         (set))))

(re/reg-sub
  :user-management/grantable-role-ids-
  :<- [:account/user-roles-]
  :<- [:user-management/roles-]
  (fn [[user-roles roles]]
    (grantable-role-ids user-roles roles)))

(re/reg-sub
  :user-management/invitations-by-email-
  :<- [:user-management/invitations-]
  (fn [invitations]
    (some->> (rows invitations)
             (group-by :invitee-email-address))))

(defn user-status
  "Where someone stands: deactivated, waiting on an invitation (pending or lapsed), or signed up. Unknown (nil) until
   invitations load."
  [{:keys [active email-address]} invitations-by-email]
  (let [invitations (get invitations-by-email (some-> email-address (s/lower-case)))]
    (cond
      (not active) :inactive
      (nil? invitations-by-email) nil
      (some :active invitations) :invited
      (not-empty invitations) :expired
      :else :active)))

(def status-labels
  {:active "Active"
   :invited "Invite sent"
   :expired "Invite expired"
   :inactive "Inactive"})

(re/reg-sub
  :user-management/filters-
  (fn [db]
    (assoc (get-in db filters-path) :tenant-organization-id (selected-organization-id db))))

(re/reg-event-db
  :user-management/set-filter re/middleware
  (fn [db [k v]]
    (assoc-in db (conj filters-path k) v)))

(re/reg-event-db
  :user-management/clear-filters re/middleware
  (fn [db]
    (update-in db filters-path select-keys [:application-id])))

(re/reg-event-db
  :user-management/show-organization-members re/middleware
  (fn [db [tenant-organization-id]]
    (let [tenant (current-tenant db)]
      (re/dispatch [:router/go [:user-management
                                {:section "users"}
                                (cond-> {:organization (str tenant-organization-id)}
                                        tenant (assoc :tenant tenant))]]))
    (update-in db filters-path select-keys [:application-id])))

(re/reg-sub
  :user-management/filtered-users-
  :<- [:user-management/users-]
  :<- [:user-management/filters-]
  :<- [:user-management/invitations-by-email-]
  (fn [[users {:keys [search tenant-organization-id status]} invitations-by-email]]
    (when-let [users (rows users)]
      (let [search (some-> search (s/trim) (s/lower-case) (not-empty))]
        (filterv #(and (or (nil? search)
                           (s/includes? (s/lower-case (:email-address %)) search))
                       (or (nil? tenant-organization-id)
                           (some #{tenant-organization-id} (:tenant-organization-ids %)))
                       (or (nil? status)
                           (nil? invitations-by-email)
                           (= status (user-status % invitations-by-email))))
                 users)))))

(re/reg-sub
  :user-management/member-counts-
  :<- [:user-management/users-]
  (fn [users]
    (->> (rows users)
         (mapcat :tenant-organization-ids)
         (frequencies))))

(re/reg-sub
  :user-management/role-user-counts-
  :<- [:user-management/users-]
  (fn [users]
    (->> (rows users)
         (mapcat :application-role-ids)
         (frequencies))))

(defn reload-lists [tenant]
  (doseq [loader [:user-management/load-users :user-management/load-invitations]]
    (re/dispatch [loader tenant :reload])))
